import { faSquareMinus, faSquarePlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLayoutEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { Link } from 'react-router-dom'
import { useTheme } from '../../theme/useTheme'
import { scrollToId } from '../../theme/utils'
import type { NavLinkProps } from '../../types/header'
import CalcomButton from './components/CalcomButton'
import ThemeToggle from './components/ThemeToggle'

function NavLink({ item, variant = 'desktop', onClick }: NavLinkProps) {
  const base = 'hover:text-gray-900 dark:hover:text-white'
  const desktop = 'text-base px-1 text-gray-600 dark:text-gray-300'
  const mobile = 'py-2 text-base text-gray-700 dark:text-gray-200'

  const className = `${variant === 'desktop' ? desktop : mobile} ${base}`

  return (
    <a
      href={`#${item.id}`}
      onClick={(e) => {
        e.preventDefault()
        if (onClick) {
          onClick()
        }
        scrollToId(item.id)
        ReactGA.event({
          category: 'Button Click',
          action: `User navigated to: ${item.label}`,
          label: 'Header',
        })
      }}
      className={className}
    >
      {item.label}
    </a>
  )
}

export default function Header() {
  const [open, setOpen] = useState(false)
  const { theme } = useTheme()

  const colours = [
    '#f5f7ff', // 0.97
    '#e9edff', // 0.83
    '#c8d3ff', // 0.78
    '#a6b7ff', // 0.76
    '#7f95ff', // 0.74
    '#6378f6', // 0.73
    '#4f60d6', // 0.69
    '#424fb0', // 0.67
    '#373f8c', // 0.65
    '#2e356f', // 0.63
    '#1a1d3f', // 0.62
    '#e9edff', // 0.59
    '#c8d3ff', // 0.58
    '#a6b7ff', // 0.45
    '#7f95ff', // 0.43
    '#4f60d6', // 0.42
    '#373f8c', // 0.38
    '#1a1d3f', // 0.36
  ]
  // prettier-ignore
  const brand = [
    { id: 0, letter: 's', },
    { id: 1, letter: 't', },
    { id: 2, letter: 'r', },
    { id: 3, letter: 'a', },
    { id: 4, letter: 'n', },
    { id: 5, letter: 'g', },
    { id: 6, letter: 'e', },
    { id: 7, letter: 'w', },
    { id: 8, letter: 'a', },
    { id: 9, letter: 'y', },
    { id: 10, letter: 's', },
  ]
  // prettier-ignore
  const division = [
    { id: 0, letter: 's', },
    { id: 1, letter: 't', },
    { id: 2, letter: 'u', },
    { id: 3, letter: 'd', },
    { id: 4, letter: 'i', },
    { id: 5, letter: 'o', },
    { id: 6, letter: 's', },
  ]

  const navItems = [
    { id: 'services', label: 'Services' },
    { id: 'work', label: 'Projects' },
    { id: 'tour', label: 'Tour' },
    { id: 'tools', label: 'Gear' },
    { id: 'rates', label: 'Rates' },
    { id: 'booking', label: 'Booking' },
    { id: 'about', label: 'About' },
    { id: 'contact', label: 'Contact' },
    { id: 'faq', label: 'FAQ' },
  ] as const

  function calculateLuminance(hexColor: string) {
    const r = parseInt(hexColor.substring(1, 3), 16) / 255
    const g = parseInt(hexColor.substring(3, 5), 16) / 255
    const b = parseInt(hexColor.substring(5, 7), 16) / 255

    const rLinear =
      r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
    const gLinear =
      g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
    const bLinear =
      b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
  }

  const lightColourList = colours.filter(
    (color) => calculateLuminance(color) > 0.25
  )
  const darkColourList = colours.filter(
    (color) => calculateLuminance(color) <= 0.75
  )

  const colourList = theme === 'light' ? darkColourList : lightColourList

  useLayoutEffect(() => {
    const svg = document.querySelector('svg')
    const path = svg?.querySelector('path') as SVGPathElement | null
    const word = document.querySelector('#brand')
    const letters =
      document.querySelectorAll<HTMLSpanElement>('#brand-name span')

    if (word && path && svg) {
      let intervalId: number | undefined

      const animateLoop = () => {
        intervalId = window.setInterval(() => {
          const randomColourIndex = Math.floor(
            Math.random() * colourList.length
          )
          letters.forEach((letter) => {
            let randomLetterColourIndex = Math.floor(
              Math.random() * colourList.length
            )
            while (randomLetterColourIndex === randomColourIndex) {
              randomLetterColourIndex = Math.floor(
                Math.random() * colourList.length
              )
            }
            letter.style.transition = 'color 2s ease-in-out'
            letter.style.color = colourList[randomLetterColourIndex]
          })
        }, 1000)
      }

      // Start animation immediately on page load
      animateLoop()

      return () => {
        if (intervalId) {
          window.clearInterval(intervalId)
        }
      }
    }
  }, [colourList, theme])

  return (
    <header
      id="header"
      className="supports-backdrop-filter:bg-white/70 z-100 sticky top-0 border-b border-gray-200 bg-white/80 backdrop-blur-md dark:border-gray-800 dark:bg-gray-950/70"
    >
      <div
        className="container-responsive vertical-align:middle flex items-center justify-between py-2"
        id="brand"
      >
        <div className="flex items-center gap-1">
          <img
            width={80}
            height={80}
            src="/tesseract-dark.svg"
            alt="Strangeways Developments"
            className="mr-1 inline-flex h-9 w-auto items-center justify-center dark:hidden"
          />
          <img
            width={80}
            height={80}
            src="/tesseract-light.svg"
            alt="Strangeways Developments"
            className="mr-1 hidden h-9 w-auto items-center justify-center dark:inline-flex"
          />
          <span className="text-xl tracking-tight sm:text-2xl">
            <Link
              to={'/'}
              onClick={(e) => {
                e.preventDefault()
                scrollToId('hero')
              }}
            >
              <div id="brand-name" className="ibm-plex-sans-700">
                {brand.map((letter) => (
                  <span className="italic" key={letter.id}>
                    {letter.letter}
                  </span>
                ))}
                <span>&nbsp;</span>
                {division.map((letter) => (
                  <span className="font-light lowercase" key={letter.id}>
                    {letter.letter}
                  </span>
                ))}
              </div>
            </Link>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <nav className="hidden items-center gap-3 md:hidden lg:flex">
            {navItems.map((item) => (
              <NavLink key={item.id} item={item} variant="desktop" />
            ))}
            <div className="flex items-center gap-3 sm:hidden">
              <CalcomButton />
              <ThemeToggle />
            </div>
          </nav>
          <div className="hidden items-center gap-3 sm:flex">
            <CalcomButton />
            <ThemeToggle />
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded border border-gray-200 p-2 text-gray-700 dark:border-gray-800 dark:text-gray-200 lg:hidden"
            aria-label="Toggle menu"
            aria-controls="mobile-menu"
            aria-expanded={open}
            onClick={() => {
              setOpen((v) => !v)
            }}
          >
            <FontAwesomeIcon
              icon={open ? faSquareMinus : faSquarePlus}
              className="text-xl"
            />
          </button>
        </div>
      </div>
      {open && (
        <nav
          id="mobile-menu"
          className="border-t border-gray-200 bg-white text-right font-bold dark:border-gray-800 dark:bg-gray-950 lg:hidden"
        >
          <div className="container-responsive flex flex-col gap-2 py-3">
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                item={item}
                variant="mobile"
                onClick={() => {
                  setOpen(false)
                }}
              />
            ))}
            <div className="block py-2 font-normal sm:hidden">
              <CalcomButton />
            </div>
            <div className="block py-2 sm:hidden">
              <ThemeToggle />
            </div>
          </div>
        </nav>
      )}
    </header>
  )
}
